import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import {
  LayoutDashboard,
  FileText,
  Users,
  BarChart3,
  PieChart,
  LogOut,
  Menu,
  X,
  Bell,
  User,
  Upload,
  UserPlus,
  Calculator,
  Building2,
  Shield,
  ChevronLeft,
  Settings,
} from 'lucide-react';
import { authService } from '../utils/auth';
import { toast } from 'sonner';
import { useFeatures } from '../context/FeaturesContext';

const SWAMI_LOGO = '/swami-logo.png';
const SESSION_CHECK_INTERVAL = 30000; // Check every 30 seconds

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const user = authService.getUser();
  const { isFeatureEnabled, userInfo, isSSOUser } = useFeatures();
  const sessionCheckRef = useRef(null);
  
  // Periodic session validity check - kicks user out immediately if disabled
  useEffect(() => {
    const checkSession = async () => {
      const isValid = await authService.checkSession();
      if (!isValid && authService.getToken()) {
        // Session was invalidated (user disabled) - forceLogout already handles redirect
      }
    };
    
    // Check immediately on mount
    checkSession();
    
    // Then check periodically
    sessionCheckRef.current = setInterval(checkSession, SESSION_CHECK_INTERVAL);
    
    return () => {
      if (sessionCheckRef.current) {
        clearInterval(sessionCheckRef.current);
      }
    };
  }, []);
  
  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);
  
  // Get user features from localStorage
  const getUserFeatures = () => {
    try {
      const userData = localStorage.getItem('user_data');
      if (userData) {
        const parsed = JSON.parse(userData);
        return parsed.features || {};
      }
    } catch {}
    return {};
  };
  
  const userFeatures = getUserFeatures();
  const isMaster = user?.role === 'master' || user?.username === 'MASTERCONTROL24';

  // Check if a feature is enabled (from passkey/user features)
  const hasFeature = (featureKey) => {
    if (isMaster) return true;
    if (!userFeatures || Object.keys(userFeatures).length === 0) return true;
    return userFeatures[featureKey] !== false;
  };

  // Define menu items with feature keys
  const allMenuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', feature: null },
    { icon: FileText, label: 'RFQ', path: '/rfq', feature: 'rfq' },
    { icon: Calculator, label: 'Costing', path: '/quotation-costing-list', feature: 'costing' },
    { icon: Users, label: 'Customers', path: '/customers', feature: 'customers' },
    { icon: UserPlus, label: 'Onboarding', path: '/potential-customers', feature: 'onboarding' },
    { icon: BarChart3, label: 'Reports', path: '/reports', feature: 'reports' },
    { icon: PieChart, label: 'Analysis', path: '/analysis', feature: 'analysis' },
    { icon: Upload, label: 'Import/Export', path: '/import-export', feature: 'import_export' },
    { icon: Settings, label: 'Settings', path: '/settings', feature: null },
  ];
  
  if (isMaster) {
    allMenuItems.push({ icon: Shield, label: 'Master Control', path: '/master', feature: null });
  }

  const menuItems = allMenuItems.filter(item => 
    item.feature === null || (isFeatureEnabled(item.feature) && hasFeature(item.feature))
  );

  // Get company name
  const getCompanyName = () => {
    if (isSSOUser && userInfo?.company_name) return userInfo.company_name;
    return user?.company_name || 'RFQ Tracker';
  };

  const handleLogout = () => {
    authService.logout();
    localStorage.removeItem('features');
    localStorage.removeItem('rfq_token');
    document.cookie = 'rfq_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    toast.success('Logged out successfully');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b z-50 flex items-center justify-between px-4">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          <Menu className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2">
          <img src={SWAMI_LOGO} alt="Logo" className="w-8 h-8" />
          <span className="font-semibold text-sm truncate max-w-[150px]">{getCompanyName()}</span>
        </div>
        <div className="flex items-center gap-2">
          <button className="p-2 rounded-lg hover:bg-gray-100 relative">
            <Bell className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </header>

      {/* Sidebar Overlay (Mobile) */}
      {isMobile && sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full bg-[#0A2540] text-white z-50
        transition-transform duration-300 ease-in-out
        w-64 lg:w-56 xl:w-64
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        {/* Sidebar Header */}
        <div className="h-14 lg:h-16 flex items-center justify-between px-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <img src={SWAMI_LOGO} alt="Logo" className="w-8 h-8 lg:w-10 lg:h-10" />
            <div className="hidden sm:block">
              <h1 className="font-bold text-sm lg:text-base leading-tight truncate max-w-[120px]">
                {getCompanyName()}
              </h1>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 rounded hover:bg-white/10"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Menu Items */}
        <nav className="p-2 lg:p-3 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 140px)' }}>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setSidebarOpen(false);
                }}
                className={`
                  w-full flex items-center gap-3 px-3 py-2.5 lg:py-2 rounded-lg text-sm
                  transition-all duration-200
                  ${isActive 
                    ? 'bg-orange-500 text-white' 
                    : 'text-gray-300 hover:bg-white/10 hover:text-white'}
                `}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                <span className="truncate">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* User Info & Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-white/10 bg-[#0A2540]">
          <div className="flex items-center gap-2 mb-2 px-2">
            <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
              <User className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{user?.username || 'User'}</p>
              <p className="text-xs text-gray-400 capitalize">{user?.role || 'User'}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-gray-300 hover:text-white hover:bg-white/10"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`
        transition-all duration-300
        pt-14 lg:pt-0
        lg:ml-56 xl:ml-64
        min-h-screen
      `}>
        {/* Desktop Header */}
        <header className="hidden lg:flex h-16 bg-white border-b items-center justify-between px-4 xl:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-800">
              {menuItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button className="p-2 rounded-lg hover:bg-gray-100 relative">
              <Bell className="w-5 h-5 text-gray-600" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg">
              <User className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium hidden xl:inline">{user?.username}</span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-3 sm:p-4 lg:p-6">
          {children}
        </div>
      </main>
    </div>
  );
}
